export type ThemeOption =
  | 'auto'
  | 'light'
  | 'dark'
  | 'midnight'
  | 'purple-dark'
  | 'bubblegum'
  | 'ocean'
  | 'forest'
  | 'aurora'
  | 'ember'
  | 'citrus'
  | 'sandstone'
  | 'slate'
  | 'glacier'
  | 'cyberpunk'
  | 'rainbow'
  | 'retro'
  | 'noir';

export interface ThemeCatalogEntry {
  value: ThemeOption;
  label: string;
  description: string;
  preview: string;
  accent: string;
}

export const THEME_CLASS_MAP: Record<ThemeOption, string | null> = {
  auto: null,
  light: 'light-theme',
  dark: 'dark-theme',
  midnight: 'midnight-theme',
  'purple-dark': 'purple-dark-theme',
  bubblegum: 'bubblegum-theme',
  ocean: 'ocean-theme',
  forest: 'forest-theme',
  aurora: 'aurora-theme',
  ember: 'ember-theme',
  citrus: 'citrus-theme',
  sandstone: 'sandstone-theme',
  slate: 'slate-theme',
  glacier: 'glacier-theme',
  cyberpunk: 'cyberpunk-theme',
  rainbow: 'rainbow-theme',
  retro: 'retro-theme',
  noir: 'noir-theme',
};

export const DARK_THEMES = new Set<ThemeOption>([
  'dark',
  'midnight',
  'purple-dark',
  'aurora',
  'ember',
  'slate',
  'cyberpunk',
  'noir',
]);

export const THEME_CATALOG: ThemeCatalogEntry[] = [
  {
    value: 'auto',
    label: 'System default',
    description: 'Automatically matches your OS appearance.',
    preview: 'linear-gradient(135deg, #0f172a 0%, #f8fafc 100%)',
    accent: '#6366f1',
  },
  {
    value: 'light',
    label: 'Daybreak',
    description: 'Soft neutrals with restrained blue accents.',
    preview: 'linear-gradient(135deg, #f7f9fc 0%, #eff4f8 55%, #e7eef5 100%)',
    accent: '#005aa8',
  },
  {
    value: 'dark',
    label: 'Deep space',
    description: 'High-contrast dark mode for low-light work.',
    preview: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    accent: '#38bdf8',
  },
  {
    value: 'midnight',
    label: 'Midnight',
    description: 'Vibrant blues over a twilight canvas.',
    preview: 'linear-gradient(135deg, #0f172a 0%, #312e81 100%)',
    accent: '#60a5fa',
  },
  {
    value: 'purple-dark',
    label: 'Neon dusk',
    description: 'Electric purple highlights for a high-energy dark theme.',
    preview: 'linear-gradient(135deg, #2d1b69 0%, #6d28d9 100%)',
    accent: '#a855f7',
  },
  {
    value: 'bubblegum',
    label: 'Bubblegum',
    description: 'Playful pink gradients with warm contrast.',
    preview: 'linear-gradient(135deg, #f472b6 0%, #fb7185 100%)',
    accent: '#fb7185',
  },
  {
    value: 'ocean',
    label: 'Ocean',
    description: 'Sea greens and teals for calmer workspaces.',
    preview: 'linear-gradient(135deg, #0f766e 0%, #38bdf8 100%)',
    accent: '#0d9488',
  },
  {
    value: 'forest',
    label: 'Forest',
    description: 'Earthy greens with rich contrast.',
    preview: 'linear-gradient(135deg, #14532d 0%, #4ade80 100%)',
    accent: '#22c55e',
  },
  {
    value: 'aurora',
    label: 'Aurora',
    description: 'Glacial blues meeting nebula purples.',
    preview: 'linear-gradient(135deg, #061024 0%, #1a2045 60%, #23d1ee 100%)',
    accent: '#22d3ee',
  },
  {
    value: 'ember',
    label: 'Ember',
    description: 'Molten oranges and ember reds for night work.',
    preview: 'linear-gradient(135deg, #2b0900 0%, #bf360c 100%)',
    accent: '#f97316',
  },
  {
    value: 'citrus',
    label: 'Citrus',
    description: 'Bright citrus zest with sunny highlights.',
    preview: 'linear-gradient(135deg, #fff7c2 0%, #facc15 100%)',
    accent: '#facc15',
  },
  {
    value: 'sandstone',
    label: 'Sandstone',
    description: 'Warm sandstone neutrals with copper accents.',
    preview: 'linear-gradient(135deg, #f8e5d3 0%, #d97706 100%)',
    accent: '#d97706',
  },
  {
    value: 'slate',
    label: 'Midnight slate',
    description: 'Graphite panels and misty grays.',
    preview: 'linear-gradient(135deg, #0d1117 0%, #273446 100%)',
    accent: '#94a3b8',
  },
  {
    value: 'glacier',
    label: 'Glacier',
    description: 'Icy blues and crisp whites.',
    preview: 'linear-gradient(135deg, #f0f7ff 0%, #38bdf8 100%)',
    accent: '#38bdf8',
  },
  {
    value: 'cyberpunk',
    label: 'Cyberpunk',
    description: 'Neon magenta and aqua on a midnight backdrop.',
    preview: 'linear-gradient(135deg, #080016 0%, #470068 60%, #22d3ee 100%)',
    accent: '#f472b6',
  },
  {
    value: 'rainbow',
    label: 'Rainbow pulse',
    description: 'A saturated full-spectrum rail with playful surfaces.',
    preview: 'linear-gradient(135deg, #f472b6 0%, #facc15 20%, #4ade80 50%, #38bdf8 80%, #a855f7 100%)',
    accent: '#a855f7',
  },
  {
    value: 'retro',
    label: 'Retro pop',
    description: 'Sorbet gradients with teal sparks.',
    preview: 'linear-gradient(135deg, #ffe2c7 0%, #ff8a4c 100%)',
    accent: '#ff8a4c',
  },
  {
    value: 'noir',
    label: 'Noir',
    description: 'High-contrast monochrome with a subtle glow.',
    preview: 'linear-gradient(135deg, #050505 0%, #1f1f1f 100%)',
    accent: '#f5f5f5',
  },
];

export const MAP_THEME_CATALOG = THEME_CATALOG.filter(
  entry => entry.value !== 'auto'
);

/**
 * SILK UI - Theme Token Contract
 */
export const REQUIRED_COLOR_TOKENS = [
  "--background", "--foreground", "--card", "--card-foreground",
  "--popover", "--popover-foreground", "--primary", "--primary-foreground",
  "--secondary", "--secondary-foreground", "--muted", "--muted-foreground",
  "--accent", "--accent-foreground", "--destructive", "--destructive-foreground",
  "--border", "--input", "--ring"
] as const;
